/**
 * Domain enums shared by API, gateway, web and (via OpenAPI) the Flutter app.
 * Values must match the Prisma enums in packages/db/prisma/schema.prisma exactly.
 */

export const TenantStatus = ['ACTIVE', 'SUSPENDED', 'TRIAL', 'CLOSED'] as const;
export type TenantStatus = (typeof TenantStatus)[number];

export const SubscriptionStatus = ['TRIAL', 'ACTIVE', 'PAST_DUE', 'SUSPENDED', 'CANCELLED'] as const;
export type SubscriptionStatus = (typeof SubscriptionStatus)[number];

export const UserKind = ['STAFF', 'DRIVER'] as const;
export type UserKind = (typeof UserKind)[number];

export const UserStatus = ['ACTIVE', 'DISABLED', 'INVITED'] as const;
export type UserStatus = (typeof UserStatus)[number];

export const DriverStatus = ['ACTIVE', 'ON_LEAVE', 'SUSPENDED', 'TERMINATED'] as const;
export type DriverStatus = (typeof DriverStatus)[number];

export const VehicleStatus = ['ACTIVE', 'MAINTENANCE', 'INACTIVE'] as const;
export type VehicleStatus = (typeof VehicleStatus)[number];

/** Computed by the ingest pipeline; never set by users. */
export const LiveStatus = ['MOVING', 'STOPPED', 'IDLE', 'OFFLINE', 'UNKNOWN'] as const;
export type LiveStatus = (typeof LiveStatus)[number];

/** Vehicle configuration: where its positions are expected to come from. */
export const TrackingSource = ['PHONE', 'OBD', 'BOTH'] as const;
export type TrackingSource = (typeof TrackingSource)[number];

/** Per-position provenance. */
export const PositionSource = ['OBD', 'PHONE', 'MANUAL'] as const;
export type PositionSource = (typeof PositionSource)[number];

export const VehicleType = [
  'CAR',
  'PICKUP',
  'VAN',
  'TRUCK',
  'TRAILER',
  'BUS',
  'MOTORCYCLE',
  'EQUIPMENT',
] as const;
export type VehicleType = (typeof VehicleType)[number];

export const FuelType = ['GASOLINE_92', 'GASOLINE_95', 'DIESEL', 'CNG', 'ELECTRIC'] as const;
export type FuelType = (typeof FuelType)[number];

export const AssignmentKind = ['PERMANENT', 'TRIP'] as const;
export type AssignmentKind = (typeof AssignmentKind)[number];

export const DeviceStatus = ['ACTIVE', 'DISABLED'] as const;
export type DeviceStatus = (typeof DeviceStatus)[number];

export const DeviceProtocol = ['TELTONIKA_CODEC8', 'QUECLINK', 'CONCOX_GT06', 'COBAN'] as const;
export type DeviceProtocol = (typeof DeviceProtocol)[number];

export const PlaceType = ['HQ', 'CUSTOMER', 'SITE', 'FUEL_STATION', 'WORKSHOP', 'HOME', 'OTHER'] as const;
export type PlaceType = (typeof PlaceType)[number];

export const PlaceShape = ['CIRCLE', 'POLYGON'] as const;
export type PlaceShape = (typeof PlaceShape)[number];

export const TripStatus = [
  'DRAFT',
  'SCHEDULED',
  'IN_PROGRESS',
  'PAUSED',
  'COMPLETED',
  'CANCELLED',
] as const;
export type TripStatus = (typeof TripStatus)[number];

export const DispatchStatus = ['NONE', 'PENDING', 'ACCEPTED', 'REJECTED'] as const;
export type DispatchStatus = (typeof DispatchStatus)[number];

export const TripStopStatus = ['PENDING', 'ARRIVED', 'DEPARTED', 'SKIPPED'] as const;
export type TripStopStatus = (typeof TripStopStatus)[number];

export const StopKind = ['STOP', 'IDLE'] as const;
export type StopKind = (typeof StopKind)[number];

export const ExpenseStatus = ['PENDING', 'APPROVED', 'REJECTED'] as const;
export type ExpenseStatus = (typeof ExpenseStatus)[number];

export const ExpenseCategoryKind = [
  'FUEL',
  'TOLL',
  'MAINTENANCE',
  'FINE',
  'ACCOMMODATION',
  'OTHER',
] as const;
export type ExpenseCategoryKind = (typeof ExpenseCategoryKind)[number];

export const InvoiceStatus = ['DRAFT', 'SENT', 'PAID', 'OVERDUE', 'CANCELLED'] as const;
export type InvoiceStatus = (typeof InvoiceStatus)[number];

export const InvoiceItemKind = ['FREIGHT', 'DISTANCE', 'WAITING', 'REBILLED_EXPENSE', 'OTHER'] as const;
export type InvoiceItemKind = (typeof InvoiceItemKind)[number];

export const MaintenanceInterval = ['KM', 'DAYS', 'ENGINE_HOURS'] as const;
export type MaintenanceInterval = (typeof MaintenanceInterval)[number];

export const WorkOrderStatus = ['OPEN', 'IN_PROGRESS', 'DONE', 'CANCELLED'] as const;
export type WorkOrderStatus = (typeof WorkOrderStatus)[number];

export const DocumentType = ['VEHICLE_LICENSE', 'INSURANCE', 'INSPECTION', 'PERMIT', 'OTHER'] as const;
export type DocumentType = (typeof DocumentType)[number];

export const AlertType = [
  // P1 — movement / operations
  'SPEEDING',
  'GEOFENCE_ENTER',
  'GEOFENCE_EXIT',
  'STOP_TOO_LONG',
  'FIRST_START_OF_DAY',
  'AFTER_HOURS_DRIVING',
  'UNAUTHORIZED_STOP',
  'ROUTE_DEVIATION',
  'DEVICE_OFFLINE',
  'PHONE_NOT_REPORTING',
  'MAINTENANCE_DUE',
  'DOCUMENT_EXPIRING',
  'DRIVER_LICENSE_EXPIRING',
  'EXPENSE_PENDING',
  'TRIP_LATE',
  'TRIP_NOT_STARTED',
  // reserved for P2 — behaviour / engine / security
  'HARSH_BRAKING',
  'HARSH_ACCELERATION',
  'HARSH_CORNERING',
  'POSSIBLE_ACCIDENT',
  'EXCESSIVE_IDLE',
  'IGNITION_ON',
  'IGNITION_OFF',
  'DTC',
  'OVERHEAT',
  'LOW_FUEL',
  'HIGH_RPM',
  'LOW_BATTERY',
  'TAMPER',
  'TOW_WITHOUT_IGNITION',
  'UNIDENTIFIED_DRIVER',
] as const;
export type AlertType = (typeof AlertType)[number];

export const AlertSeverity = ['INFO', 'WARNING', 'CRITICAL'] as const;
export type AlertSeverity = (typeof AlertSeverity)[number];

export const AlertEventStatus = ['OPEN', 'ACKNOWLEDGED', 'CLOSED'] as const;
export type AlertEventStatus = (typeof AlertEventStatus)[number];

export const NotificationChannel = ['IN_APP', 'PUSH', 'EMAIL', 'SMS', 'WHATSAPP'] as const;
export type NotificationChannel = (typeof NotificationChannel)[number];

export const NotificationStatus = ['PENDING', 'SENT', 'FAILED', 'READ'] as const;
export type NotificationStatus = (typeof NotificationStatus)[number];

export const AuditAction = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'LOGIN',
  'LOGOUT',
  'APPROVE',
  'REJECT',
  'DISPATCH',
  'STATUS_CHANGE',
  'EXPORT',
] as const;
export type AuditAction = (typeof AuditAction)[number];

export const Locale = ['AR', 'EN'] as const;
export type Locale = (typeof Locale)[number];
